import { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetUserProfileStore, useSendCodeToEmailStore, useSendCodeToMobileStore, useUpdateProfileStore, useUserStore } from '@lib/store';
import { useAccountContext } from '@shared/hooks';
import { Block } from './Block';
import { Address } from './Address';
import { Button, InputPreview, PhoneInput, Spinner, TextInput } from '@shared/widgets';

export enum AddressEnum {
  Primary = 'Primary',
  Secondary = 'Secondary',
}

interface IDetailsEditorForm {
  email: string;
  emailCode: string;
  mobileNumber: { code: string; number: string };
  mobileCode: string;
}
const DETAILS_VALUES: IDetailsEditorForm = {
  email: '',
  emailCode: '',
  mobileNumber: { code: '', number: '' },
  mobileCode: '',
};

interface DetailsEditorProps {
  approval?: boolean;
}

export const DetailsEditor = (props: DetailsEditorProps) => {
  const userStore = useUserStore();
  const newVersion = userStore.data?.version === 'v1';
  const userProfileStore = useGetUserProfileStore();
  const sendCodeToEmailStore = useSendCodeToEmailStore();
  const sendCodeToMobileStore = useSendCodeToMobileStore();
  const updateProfileStore = useUpdateProfileStore();

  const [mobileChanged, setMobileChanged] = useState<boolean>(false);
  const [emailChanged, setEmailChanged] = useState<boolean>(false);
  const [confirmEmail, setConfirmEmail] = useState<boolean>(false);
  const [confirmMobile, setConfirmMobile] = useState<boolean>(false);

  const { account } = useAccountContext();
  const { t } = useTranslation('profile');

  const detailsEditorForm = useForm({ defaultValues: DETAILS_VALUES });
  const email = detailsEditorForm.watch('email');
  const emailCode = detailsEditorForm.watch('emailCode');
  const mobileCode = detailsEditorForm.watch('mobileCode');
  const mobileNumber = detailsEditorForm.watch('mobileNumber');

  useEffect(() => {
    if (account) userProfileStore.get(account.id.toString());
  }, [account]);

  useEffect(() => {
    if (userProfileStore.data) {
      detailsEditorForm.setValue('mobileNumber.code', userProfileStore.data.mobilePhoneCountryCodePart!);
      detailsEditorForm.setValue('mobileNumber.number', userProfileStore.data.mobilePhoneNumberPart!);
      detailsEditorForm.setValue('email', userProfileStore.data.email);
    }
  }, [userProfileStore.data]);

  useEffect(() => {
    if (userProfileStore.data?.email) {
      const isEmailChanged = email !== userProfileStore.data.email;
      setEmailChanged(isEmailChanged);

      if (!isEmailChanged) setConfirmEmail(false);
    }
  }, [email, userProfileStore.data?.email]);

  useEffect(() => {
    if (userProfileStore.data?.mobilePhoneCountryCodePart) {
      const isCodeChanged = mobileNumber.code !== userProfileStore.data.mobilePhoneCountryCodePart;
      setMobileChanged(isCodeChanged);

      if (!isCodeChanged) setConfirmMobile(false);
    }
  }, [mobileNumber.code, userProfileStore.data?.mobilePhoneCountryCodePart]);

  useEffect(() => {
    if (userProfileStore.data?.mobilePhoneNumberPart) {
      const isNumberChanged = mobileNumber.number !== userProfileStore.data.mobilePhoneNumberPart;
      setMobileChanged(isNumberChanged);

      if (!isNumberChanged) setConfirmMobile(false);
    }
  }, [mobileNumber.number, userProfileStore.data?.mobilePhoneNumberPart]);

  useEffect(() => {
    return () => {
      updateProfileStore.clear();
    };
  }, []);

  const onChangeEmail = async () => {
    sendCodeToEmailStore.send({ email });
    setEmailChanged(false);
    setConfirmEmail(true);
  };
  const onChangeMobile = async () => {
    sendCodeToMobileStore.send({ phoneNumber: mobileNumber.number, areaCode: mobileNumber.code });
    setMobileChanged(false);
    setConfirmMobile(true);
  };
  const handleConfirmEmail = () => {
    if (!sendCodeToEmailStore.data || !account) return;

    updateProfileStore.update(
      { code: emailCode, email: email, hash: sendCodeToEmailStore.data?.hash, ticks: sendCodeToEmailStore.data?.ticks },
      account.id,
    );
  };
  const handleConfirmMobile = () => {
    if (!sendCodeToMobileStore.data || !account) return;

    updateProfileStore.update(
      {
        code: mobileCode,
        mobileCountryCode: mobileNumber.code,
        mobileNumber: mobileNumber.number,
        hash: sendCodeToMobileStore.data?.hash,
        ticks: sendCodeToMobileStore.data?.ticks,
      },
      account.id,
    );
  };

  const TextField = props.approval || newVersion ? InputPreview : TextInput;
  return (
    <div className='border border-[#e1e1e1] p-10 max-[840px]:p-5'>
      {props.approval && <Button className='hidden' caption={t('buttons.back', { ns: 'common' })} />}
      <form noValidate>
        <Block className='max-w-[652px] max-lg:max-w-full' title={t('personalDetails.title')}>
          <div className='-mr-2.5  max-lg:flex max-lg:flex-col'>
            <div className='inline-block pr-2.5 w-1/2 max-lg:w-full max-lg:pr-0'>
              <TextInput
                caption={t('placeholders.name', { ns: 'common' })}
                formStyle
                name='name'
                required
                readOnly={newVersion}
                value={userProfileStore.data?.firstName}
              />
            </div>
            <div className='inline-block pl-2.5 w-1/2 max-lg:w-full max-lg:pl-0'>
              <TextInput
                caption={t('placeholders.surname', { ns: 'common' })}
                formStyle
                name='surname'
                required
                readOnly={newVersion}
                value={userProfileStore.data?.lastName}
              />
            </div>
          </div>
        </Block>
        <div className='max-w-[1120px] -mx-8 max-lg:flex max-lg:flex-col'>
          <div className='inline-block px-8 w-1/2 align-top max-lg:w-full'>
            <Address data={userProfileStore.primaryData} approval={props.approval!} readOnly={newVersion} type={AddressEnum.Primary} />
          </div>
          <div className='inline-block px-8 w-1/2 align-top max-lg:w-full'>
            <Address data={userProfileStore.secondaryData} approval={props.approval!} readOnly={newVersion} type={AddressEnum.Secondary} />
          </div>
        </div>
        <Block title={t('personalDetails.contactDetails.title')}>
          <div className='max-w-[978px] -mx-2.5 max-lg:flex max-lg:flex-col max-[840px]:mx-0 max-[840px]:gap-5'>
            {!newVersion && (
              <div className='inline-block px-2.5 max-lg:px-0 w-1/3 align-top'>
                <TextField
                  caption={t('personalDetails.contactDetails.placeholders.telephone')}
                  value={userProfileStore.data!.mobilePhoneCountryCodePart!}
                />
              </div>
            )}
            <div className='inline-block px-2.5 max-lg:px-0  w-1/3 align-top max-lg:w-full'>
              {!newVersion ? (
                <TextField caption={t('placeholders.mobileNumber', { ns: 'common' })} value={userProfileStore.data!.mobilePhoneNumberPart!} />
              ) : (
                <>
                  <Controller
                    name='mobileNumber'
                    control={detailsEditorForm.control}
                    rules={{
                      required: t('errors.form.required', { ns: 'common' }),
                      validate: (value) => {
                        if (!value.code) return t('errors.form.required', { ns: 'common' });
                        if (!value.number) return t('errors.form.required', { ns: 'common' });
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <PhoneInput
                        {...field}
                        className='w-[130px]'
                        formStyle
                        caption={`${t('placeholders.mobileNumber', { ns: 'common' })}:`}
                        name='mobileNumber'
                        required
                        error={error?.message}
                      />
                    )}
                  />
                  <div>
                    {sendCodeToMobileStore.loading && <Spinner className='min-h-[200px]' dark />}
                    {updateProfileStore.success && confirmMobile ? (
                      t('personalDetails.contactDetails.successMessages.mobileNumberChanged')
                    ) : (
                      <>
                        {mobileChanged && !confirmMobile && (
                          <Button
                            className='mt-6'
                            fullWidth
                            view='secondary'
                            caption={t('buttons.update', { ns: 'common' })}
                            type='button'
                            onClick={onChangeMobile}
                          />
                        )}
                        {confirmMobile && (
                          <>
                            <Controller
                              name='mobileCode'
                              control={detailsEditorForm.control}
                              rules={{
                                required: t('errors.form.requiredField', {
                                  ns: 'common',
                                  field: t('placeholders.confirmationCode', { ns: 'common' }),
                                }),
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <TextInput
                                  {...field}
                                  formStyle
                                  caption={t('placeholders.confirmationCode', { ns: 'common' })}
                                  required
                                  error={error?.message}
                                />
                              )}
                            />
                            <Button
                              fullWidth
                              view='secondary'
                              caption={t('buttons.confirm', { ns: 'common' })}
                              type='button'
                              onClick={handleConfirmMobile}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className='inline-block px-2.5 max-lg:px-0  w-1/3 align-top max-lg:w-full'>
              <Controller
                name='email'
                control={detailsEditorForm.control}
                rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.email', { ns: 'common' }) }) }}
                render={({ field, fieldState: { error } }) => (
                  <TextInput {...field} formStyle caption={`${t('placeholders.emailAddress', { ns: 'common' })}:`} required error={error?.message} />
                )}
              />
              <div>
                {sendCodeToEmailStore.loading && <Spinner className='min-h-[200px]' dark />}
                {updateProfileStore.success && confirmEmail ? (
                  t('personalDetails.contactDetails.successMessages.emailChanged')
                ) : (
                  <>
                    {emailChanged && !confirmEmail && (
                      <Button fullWidth view='secondary' caption={t('buttons.update', { ns: 'common' })} type='button' onClick={onChangeEmail} />
                    )}
                    {confirmEmail && (
                      <>
                        <Controller
                          name='emailCode'
                          control={detailsEditorForm.control}
                          rules={{
                            required: t('errors.form.requiredField', { ns: 'common', field: t('placeholders.confirmationCode', { ns: 'common' }) }),
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <TextInput
                              {...field}
                              formStyle
                              caption={t('placeholders.confirmationCode', { ns: 'common' })}
                              required
                              error={error?.message}
                            />
                          )}
                        />
                        <Button
                          fullWidth
                          view='secondary'
                          caption={t('buttons.confirm', { ns: 'common' })}
                          type='button'
                          onClick={handleConfirmEmail}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </Block>
        {!props.approval && !newVersion && (
          <>
            <div>{t('personalDetails.hint')}</div>
            <div>
              <Button className='text-[#4c4c52] ml-auto min-w-[109px]' caption={t('buttons.submit', { ns: 'common' })} type='submit' />
            </div>
          </>
        )}
      </form>
    </div>
  );
};
